import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Loader2, Mail, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fmtUSD2 } from "@/lib/api";

export default function SendQuoteDialog({ open, onOpenChange, quote }) {
  const qc = useQueryClient();
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);

  useEffect(() => {
    if (!quote) return;
    setTo(quote.contactEmail || "");
    setSubject(`Propuesta ${quote.quoteNumber} · Mediana Magazine`);
    setMessage(`Hola ${quote.contactName || ""},\n\nAdjuntamos la propuesta ${quote.quoteNumber}. Podés revisarla y aceptarla con firma desde el enlace. Quedamos atentos a tus comentarios.\n\nGracias,\nEquipo Comercial · Mediana`);
  }, [quote]);

  const send = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${quote.id}/send-email`, { to, subject, message, attachPdf })).data,
    onSuccess: (r) => {
      toast.success(`Propuesta enviada a ${to}${r?.attachedPdf ? (r.pdfEngine === "canva" ? " con PDF de Canva" : " con PDF adjunto") : ""}`);
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["guide-first-deal"] });
      qc.invalidateQueries({ queryKey: ["quotes", quote?.dealId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!quote) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="send-quote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> Enviar propuesta por Gmail</DialogTitle>
          <DialogDescription>
            Se envía desde tu cuenta de Gmail con un enlace público para aceptar con firma. Total: {fmtUSD2(quote.total)}.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5"><Label className="text-xs">Para</Label><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="send-quote-to" /></div>
          <div className="grid gap-1.5"><Label className="text-xs">Asunto</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="send-quote-subject" /></div>
          <div className="grid gap-1.5"><Label className="text-xs">Mensaje</Label><Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="send-quote-message" /></div>
          <label className="flex items-start gap-2 rounded-md border border-border bg-background px-3 py-2.5 text-[12.5px]">
            <Checkbox checked={attachPdf} onCheckedChange={(v) => setAttachPdf(!!v)} className="mt-0.5" data-testid="send-quote-attach-pdf" />
            <span>
              <span className="flex items-center gap-1 font-medium"><Paperclip className="h-3.5 w-3.5" /> Adjuntar la propuesta en PDF</span>
              <span className="block text-[11.5px] text-muted-foreground">
                {quote.pdfFileId && quote.pdfFresh ? "Se adjunta el PDF ya generado." : "Se genera al enviar (Canva si está conectado; si no, diseño Mediana). Puede tardar unos segundos."}
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => send.mutate()} disabled={!to || !subject || send.isPending} data-testid="send-quote-button">
            {send.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Enviar propuesta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
